import type React from 'react'
import { useCallback, useRef, useState } from 'react'
import { toCanvasPoint } from '@/components/app/editor/tools/shared/helpers/svg-point'
import { hitTestPenHover } from '../helpers/pen-hover-hit-test'
import type { PenHoverTarget } from '../types/node-edit-targets'
import type { NodeEditPropRefs } from './use-node-edit-prop-refs'
import type { NodePathState } from './use-node-path-state'

interface UsePenEditHoverOptions {
  svgRef: React.RefObject<SVGSVGElement | null>
  path: NodePathState
  propRefs: NodeEditPropRefs
  /** The shape prop's origin — the hit test offsets the shape-local points by it. */
  shapeX: number
  shapeY: number
  scale: number
  bleedWidth: number
  bleedHeight: number
}

/** Pen-edit mode hover computation: what the cursor is over, kept in state (for rendering) and a
 *  ref (for the window listeners), plus the capture rect's move/leave handlers. */
export function usePenEditHover({
  svgRef,
  path,
  propRefs,
  shapeX,
  shapeY,
  scale,
  bleedWidth,
  bleedHeight,
}: UsePenEditHoverOptions) {
  const [penHover, setPenHover] = useState<PenHoverTarget>(null)
  const penHoverRef = useRef<PenHoverTarget>(null)
  const { onPenHoverChangeRef } = propRefs

  const computePenHover = useCallback(
    (clientX: number, clientY: number): PenHoverTarget => {
      const { x, y } = toCanvasPoint(svgRef.current, clientX, clientY, bleedWidth, bleedHeight)

      return hitTestPenHover({
        canvasX: x,
        canvasY: y,
        points: path.pointsRef.current,
        isClosed: path.isClosedRef.current,
        subpaths: path.subpathsRef.current,
        ox: shapeX,
        oy: shapeY,
        scale,
        rotation: propRefs.shapeRotationRef.current,
        geom: path.shapeGeomRef.current,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shapeX, shapeY, scale]
  )

  const computePenHoverRef = useRef(computePenHover)

  computePenHoverRef.current = computePenHover

  const handlePenEditMove = useCallback(
    (e: React.PointerEvent) => {
      const hover = computePenHover(e.clientX, e.clientY)

      penHoverRef.current = hover
      setPenHover(hover)
      onPenHoverChangeRef.current?.(hover ? hover.kind : null)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [computePenHover]
  )

  const handlePenEditLeave = useCallback(() => {
    penHoverRef.current = null
    setPenHover(null)
    onPenHoverChangeRef.current?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    penHover,
    setPenHover,
    penHoverRef,
    computePenHoverRef,
    handlePenEditMove,
    handlePenEditLeave,
  }
}

export type PenEditHover = ReturnType<typeof usePenEditHover>
